// CABECERA DEL RADAR OPERATIVO — identidad Galarya + estado de la vista + resumen operacional
// + selector de densidad. No es un dashboard: sin gráficas, tendencias ni KPIs nuevos.
// VIS-01 · sólo se reordenó la JERARQUÍA de lo que ya existía, en el orden de lectura del
// contrato: 1) identidad de la pantalla, 2) situación operacional, 3) herramientas de
// exploración, 4) personas (el grid vive en la página). Mismos datos, mismos cálculos, mismo
// comportamiento de búsqueda y de densidad. En móvil el bloque ocupa menos alto para que las
// fichas entren antes en el viewport.
import { Users, AlertTriangle, UserCheck, Clock, ShieldAlert, Radio, WifiOff, Search, LayoutGrid } from "lucide-react";
import { DENSIDADES } from "@/lib/radar/densidad";
import SesionIdentidad from "@/components/radar/SesionIdentidad";

// Métrica existente del resumen. El énfasis es SEMÁNTICO, no decorativo: el número toma el color
// del indicador sólo cuando hay algo que revisar (>0); en cero se mantiene en el azul institucional.
function Stat({ n, label, corta, tone, num, ico, icon: Icon, testId, title }) {
  const hay = Number(n) > 0;
  const numTone = hay && num ? num : "text-[#012472]";
  return (
    <div data-testid={testId} title={title || label}
      className="flex min-w-0 flex-1 items-center gap-1.5 border-l border-slate-100 pl-2 first:border-l-0 first:pl-0 sm:flex-none sm:shrink-0 sm:gap-2 sm:pl-3">
      {/* En móvil el indicador va inline (cabe la población completa sin desplazar); desde sm
          recupera su pastilla. Mismos cinco indicadores, mismos valores. */}
      <span className={`hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:flex ${tone}`}><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 leading-none">
        <p className={`flex items-center gap-1 text-base font-extrabold tabular-nums sm:text-xl ${numTone}`}>
          <Icon className={`h-3 w-3 shrink-0 sm:hidden ${ico}`} />{n}
        </p>
        <p className="mt-1 truncate text-[9px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[10px]">
          <span className="sm:hidden">{corta || label}</span>
          <span className="hidden sm:inline">{label}</span>
        </p>
      </div>
    </div>
  );
}

export default function RadarHeader({ resumen, live, densidad, densidadEfect, autoExp, onDensidad, q, onQ, ventanaMin, controles = null }) {
  return (
    <div className="border-b border-slate-200 bg-white/95 backdrop-blur">
      {/* 1 · IDENTIDAD DEL MÓDULO + SITUACIÓN DE LA VISTA.
          VIS-01R2 · aquí ya NO va el logo Galarya (vivía dos veces en pantalla, junto al del
          header global): el módulo se identifica con su propio icono y su nombre. */}
      <div className="flex items-center gap-2.5 px-4 py-2 sm:gap-3 sm:px-5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#01CBE3]/12 text-[#0891A6]">
          <LayoutGrid className="h-5 w-5" strokeWidth={1.9} />
        </span>
        <div className="min-w-0 leading-tight">
          <h1 data-testid="radar-titulo" className="truncate text-base font-extrabold tracking-tight text-[#012472] md:text-lg">
            Radar Operativo
          </h1>
          <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-400">Residencial</p>
        </div>
        <span data-testid="radar-estado-vista"
          title={live
            ? "La vista se actualiza automáticamente. No implica que la cobertura de los sensores esté confirmada."
            : "La vista dejó de actualizarse. Lo que ves puede no reflejar la situación actual."}
          className={`ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide ${live ? "bg-[#01CBE3]/15 text-[#0891A6]" : "bg-red-100 text-red-700"}`}>
          {live ? <Radio className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">{live ? "VISTA EN VIVO" : "VISTA SIN ACTUALIZAR"}</span>
          <span className="sm:hidden">{live ? "EN VIVO" : "SIN ACTUALIZAR"}</span>
        </span>
        {/* V1 · identidad REAL de la sesión (nombre + rol) junto a la cabecera existente. */}
        <SesionIdentidad testId="radar-res-sesion-identidad" />
      </div>

      {/* 2 · ESTADO OPERACIONAL — las cinco métricas YA existentes, leídas como un solo bloque.
          Mismos valores y mismo significado; en móvil caben las cinco en una sola línea (antes
          se apilaban en tres filas) sin ocultar ninguna. */}
      <div data-testid="radar-estado-operacional"
        className="flex items-center gap-1.5 border-t border-slate-100 px-4 py-2 sm:gap-3 sm:px-5">
        <Stat n={resumen.personas} label="Personas" tone="bg-[#012472]/10 text-[#012472]" ico="text-[#012472]" icon={Users} testId="radar-stat-personas" />
        <Stat n={resumen.pendientes} label="Pendientes" tone="bg-[#FE6A55]/15 text-[#FE6A55]" num="text-[#FE6A55]" ico="text-[#FE6A55]" icon={AlertTriangle} testId="radar-stat-pendientes" />
        <Stat n={resumen.en_atencion} label="En atención" corta="Atención" tone="bg-[#01CBE3]/15 text-[#0891A6]" num="text-[#0891A6]" ico="text-[#0891A6]" icon={UserCheck} testId="radar-stat-atencion" />
        <Stat n={resumen.actividad_reciente} label={`Actividad reciente · ${ventanaMin} min`} corta={`Act. ${ventanaMin}m`}
          title={`Personas con actividad observada en los últimos ${ventanaMin} minutos. Es contexto: no crea casos.`}
          tone="bg-[#0284C7]/12 text-[#0284C7]" ico="text-[#0284C7]" icon={Clock} testId="radar-stat-actividad" />
        <Stat n={resumen.coberturas_revisar} label="Coberturas a revisar" corta="Cobertura"
          tone="bg-[#9B2C2C]/12 text-[#9B2C2C]" num="text-[#9B2C2C]" ico="text-[#9B2C2C]" icon={ShieldAlert} testId="radar-stat-cobertura" />
      </div>

      {/* 3 · HERRAMIENTAS DE EXPLORACIÓN — búsqueda y densidad del grid. No son estado
          operacional: viven separadas de las métricas y con menos peso visual. */}
      <div className="flex items-center gap-2 border-t border-slate-100 px-4 py-2 sm:px-5">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 sm:max-w-xs">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input data-testid="radar-busqueda" value={q} onChange={(e) => onQ(e.target.value)}
            placeholder="Buscar por nombre o sitio" className="w-full min-w-0 bg-transparent text-sm text-slate-700 outline-none" />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <span className="hidden items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:flex">
            <LayoutGrid className="h-3 w-3" /> Vista
          </span>
          <div data-testid="radar-densidad" className="inline-flex items-center gap-0.5 rounded-xl bg-slate-100 p-0.5 sm:gap-1 sm:p-1">
            {DENSIDADES.map((d) => {
              const activo = String(densidad) === String(d);
              return (
                <button key={String(d)} type="button" data-testid={`radar-densidad-${d}`}
                  onClick={() => onDensidad(d)}
                  aria-pressed={activo}
                  title={d === "todos" ? "Ver toda la población" : `Ver ${d} fichas`}
                  className={`min-h-9 min-w-8 rounded-lg px-1.5 text-[13px] font-bold transition-colors sm:min-w-11 sm:px-2.5 sm:text-sm ${activo ? "bg-white text-[#012472] shadow-sm" : "text-slate-500 hover:text-[#012472]"}`}>
                  {d === "todos" ? "Todos" : d}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {autoExp && (
        <p data-testid="radar-auto-expandido" className="border-t border-slate-100 px-4 py-1 text-[11px] font-semibold text-[#9B2C2C] sm:px-5">
          Ampliado a {densidadEfect === "todos" ? "Todos" : densidadEfect} para no ocultar atenciones activas
        </p>
      )}

      {/* R1/R3/R4 · controles de cartera, orden y selección personal. Viven DENTRO de la cabecera
          existente: no se añade otra barra, ni otro logo, ni otra navegación. */}
      {controles}
    </div>
  );
}
